// DashboardPage.tsx
// Main dashboard page component

import React, { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BellDot, BarChart2, Webhook, ListChecks, Megaphone } from "lucide-react";
import { AnalyticsView } from "./AnalyticsView";
import { TemplatesBoard } from "./TemplatesBoard";
import { seedFoldersCampaign, seedFoldersAutomatic, seedNotifications } from "./seedData";
import { PushBoard } from "./Pushboard";

// Storage functions (kept for compatibility with original code)
const LS_KEY = "pushcenter_state_v3";
const loadState = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const saveState = (state: any) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {}
};

const TopNav = ({ tab, setTab }: { tab: string; setTab: (s: string) => void }) => (
  <div className="sticky top-0 z-10 -mx-1 -mt-1 bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/60">
    <div className="mx-auto max-w-[1200px] px-1">
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mt-3 grid w-full grid-cols-4 rounded-2xl p-1">
          <TabsTrigger value="analytics" className="rounded-xl">
            <div className="flex items-center gap-2">
              <BarChart2 className="h-4 w-4" /> Analytics
            </div>
          </TabsTrigger>
          <TabsTrigger value="campaign" className="rounded-xl">
            <div className="flex items-center gap-2">
              <Megaphone className="h-4 w-4" /> Campaign
            </div>
          </TabsTrigger>
          <TabsTrigger value="automatic" className="rounded-xl">
            <div className="flex items-center gap-2">
              <Webhook className="h-4 w-4" /> Automatic
            </div>
          </TabsTrigger>
          <TabsTrigger value="templates" className="rounded-xl">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4" /> Templates
            </div>
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  </div>
);

const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="mx-auto max-w-[1200px] space-y-6 px-4 py-6">
    <div className="flex items-center gap-4">
      <div className="flex h-14 w-14 items-center justify-center rounded-xl overflow-hidden bg-white shadow-sm">
        <img 
          src="https://startupcity.hamburg/logos/startups/kalia_lab.png" 
          alt="Logo"
          className="h-full w-full object-cover"
        />
      </div>
      <div>
        <div className="text-xl font-semibold leading-tight">
          Push Notification Center
        </div>
        <div className="text-xs text-muted-foreground">
          Create, schedule, analyze, and manage push notifications
        </div>
      </div>
    </div>
    {children}
  </div>
);

const DashboardPage: React.FC = () => {
  const [tab, setTab] = useState("campaign");
  const [foldersCampaign, setFoldersCampaign] = useState(
    () => loadState()?.foldersCampaign ?? seedFoldersCampaign
  );
  const [foldersAutomatic, setFoldersAutomatic] = useState(
    () => loadState()?.foldersAutomatic ?? seedFoldersAutomatic
  );
  const [notifications, setNotifications] = useState(
    () => loadState()?.notifications ?? seedNotifications
  );

  useEffect(() => {
    saveState({ foldersCampaign, foldersAutomatic, notifications });
  }, [foldersCampaign, foldersAutomatic, notifications]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <TopNav tab={tab} setTab={setTab} />
      <Shell>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsContent value="analytics" className="space-y-6">
            <AnalyticsView />
          </TabsContent>

          <TabsContent value="campaign" className="space-y-6">
            <PushBoard type="campaign" />
          </TabsContent>

          <TabsContent value="automatic" className="space-y-6">
            <PushBoard type="automatic" />
          </TabsContent>

          <TabsContent value="templates" className="space-y-6">
            <TemplatesBoard />
          </TabsContent>
        </Tabs>
      </Shell>
    </div>
  );
};

export default DashboardPage;